"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./threeSteps.module.scss";
import dynamic from "next/dynamic";
import type { LottieRefCurrentProps } from "lottie-react";
import TitleWithDescriptionV2 from "../titleWithDescriptionV2/titleWithDescriptionV2";
import { threeSteps } from "@/lib/homePage";
import RoundedButton from "../roundedButton/roundedButton";
import { svgImages } from "@/lib/svgImages";
import Image from "next/image";
import ProductSteps from "../productSteps/productSteps";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

// Register GSAP plugins at the top level
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export const ThreeSteps = () => {
  const leftElementsRef = useRef<(HTMLDivElement | null)[]>([]);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const leftElementTitlesRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [animations, setAnimations] = useState({
    create: null,
    customize: null,
    publish: null,
  });

  // Refs for Lottie animations and their containers
  const createLottieRef = useRef<LottieRefCurrentProps>(null);
  const customizeLottieRef = useRef<LottieRefCurrentProps>(null);
  const publishLottieRef = useRef<LottieRefCurrentProps>(null);

  const lottieContainersRef = useRef<(HTMLDivElement | null)[]>([]);
  const scrollTriggersRef = useRef<ScrollTrigger[]>([]);

  const [productSteps, setProductSteps] = useState<any>(null);

  useEffect(() => {
    // Remove the registration since it's now at the top level
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    ScrollTrigger.refresh();

    (async () => {
      const data = await fetch("/lib/productSteps.json").then((res) =>
        res.json()
      );
      setProductSteps(data);
    })();
  }, []);

  // Create a debounced resize handler for performance
  const debounce = (callback: Function, delay = 100) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  };

  // Setup GSAP animations and ScrollTrigger instances
  const setupAnimations = useCallback(() => {
    try {
      if (isLoading) return;

      // Kill any existing ScrollTrigger instances
      scrollTriggersRef.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
      scrollTriggersRef.current = [];

      const leftElements = leftElementsRef.current.filter(Boolean);
      const titleElements = leftElementTitlesRef.current.filter(Boolean);

      if (!leftElements.length || !titleElements.length) {
        console.warn("Required elements not found for animations");
        return;
      }

      // Only set initial state for Lottie containers
      gsap.set(lottieContainersRef.current, { opacity: 0 });

      // Create the pin ScrollTrigger only if elements exist
      if (leftElements[0]?.parentElement && rightContainerRef.current) {
        const pinTrigger = ScrollTrigger.create({
          trigger: leftElements[0].parentElement,
          start: "275px center",
          end: "bottom-=275px center",
          pin: rightContainerRef.current,
          // markers: true,
        });
        scrollTriggersRef.current.push(pinTrigger);
      }

      // Desktop animations - only for Lottie containers
      titleElements.forEach((titleEl, index) => {
        if (!titleEl) return;

        const trigger = ScrollTrigger.create({
          trigger: titleEl,
          start: titleElements[0] === titleEl ? "-200px 60%" : "+200px 60%",
          end: "bottom 60%",
          onEnter: () => {
            if (!lottieContainersRef.current[index]) return;

            gsap.to(lottieContainersRef.current, { opacity: 0, duration: 0.3 });

            [createLottieRef, customizeLottieRef, publishLottieRef].forEach(
              (ref) => {
                ref.current?.pause();
              }
            );

            gsap.to(lottieContainersRef.current[index], {
              opacity: 1,
              duration: 0.3,
              onStart: () => {
                const currentRef = [
                  createLottieRef,
                  customizeLottieRef,
                  publishLottieRef,
                ][index];
                currentRef.current?.goToAndStop(0, true);
                currentRef.current?.play();
              },
            });
          },
          onLeaveBack: () => {
            if (index > 0) {
              gsap.to(lottieContainersRef.current[index], {
                opacity: 0,
                duration: 0.3,
              });
              const prevRef = [
                createLottieRef,
                customizeLottieRef,
                publishLottieRef,
              ][index - 1];
              prevRef.current?.goToAndPlay(0);
              gsap.to(lottieContainersRef.current[index - 1], {
                opacity: 1,
                duration: 0.3,
              });
            }
          },
        });
        scrollTriggersRef.current.push(trigger);
      });
    } catch (error) {
      console.error("Error setting up animations:", error);
    }
  }, [isLoading]);

  useEffect(() => {
    let isActive = true;

    // Load all animations
    Promise.all(
      threeSteps.map((step) => {
        return fetch(
          `/images/home/threeSteps/${step.title.toLocaleLowerCase()}.json`
        ).then((res) => res.json());
      })
    ).then(([createData, customizeData, publishData]) => {
      if (!isActive) return;
      setAnimations({
        create: createData,
        customize: customizeData,
        publish: publishData,
      });

      // Add a small delay to ensure content is rendered
      setTimeout(() => {
        if (!isActive) return;
        setIsLoading(false);
        setupAnimations();
      }, 100);
    });

    // Create a debounced resize handler with 200ms delay for better performance
    const debouncedHandleResize = debounce(() => {
      if (!isActive) return;
      ScrollTrigger.refresh();
      setupAnimations();
    }, 200);

    window.addEventListener("resize", debouncedHandleResize);

    return () => {
      isActive = false;
      window.removeEventListener("resize", debouncedHandleResize);
      scrollTriggersRef.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
      scrollTriggersRef.current = [];
      gsap.killTweensOf(lottieContainersRef.current);
    };
  }, [setupAnimations]);

  return (
    <div className={`${styles["main"]} `}>
      <div className="custom-container">
        <div className="inner">
          <div className={`${styles["headlineContainer"]} d-flex-lg d-none-xs`}>
            <TitleWithDescriptionV2
              title="Simple Steps.<span style='display: block;'>Endless Possibilities</span>"
              description="Build your dynamic QR page in 3 simple steps. Just click to add text, images, videos and more exactly as it appears."
            />
          </div>
          {/* Desktop Version */}
          <div className={`${styles["contentContainer"]} d-flex-lg d-none-xs`}>
            <div className={`${styles["left"]}`}>
              {threeSteps.map((step, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    leftElementsRef.current[index] = el;
                  }}
                  className={`${styles["leftElement"]}`}
                >
                  <div
                    className={`${styles["leftElementTitle"]} line-height-title`}
                    ref={(el) => {
                      leftElementTitlesRef.current[index] = el;
                    }}
                  >
                    <div className={`${styles["title"]} `}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: step.icon,
                        }}
                      />
                      <h3 className={`font-size-24`}>{step.title}</h3>
                    </div>
                  </div>
                  <h4 className={`font-size-48`}>{step.subtitle}</h4>
                  <p
                    className={`${styles["leftElementDescription"]} font-size-18`}
                  >
                    {step.description}
                  </p>
                  <ul className={`${styles["leftElementList"]}`}>
                    {step.list.map((item) => (
                      <li
                        key={item}
                        className={`font-size-20 line-height-content`}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div
                    className={`${styles["leftElementHighlighted"]} font-size-20`}
                    dangerouslySetInnerHTML={{ __html: step.highlighted }}
                  />
                  <div className={`${styles["leftElementButton"]}`}>
                    <RoundedButton
                      title="Try for free"
                      url={process.env.NEXT_PUBLIC_DASHBOARD_URL}
                      target={"_blank"}
                      light={false}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div ref={rightContainerRef} className={`${styles["right"]}`}>
              <div className={`${styles["rightElement"]}`}>
                <div className={`${styles["rightElement__bg"]}`}>
                  <div
                    className={`${styles["rightElement__bg__gradientContainer"]}`}
                  >
                    <Image
                      className={`${styles["rightElement__bg__gradientContainer__gradientImage"]}`}
                      src="/images/gradient-balls.svg"
                      alt="bg-gradient"
                      width={1000}
                      height={1000}
                    />
                  </div>
                  <div
                    className={`${styles["right-element__bg__glass-container"]}`}
                  >
                    {Array.from({ length: 18 }).map((_, i) => (
                      <div
                        key={i}
                        className={`${styles["right-element__bg__glass-container__glass-element"]}`}
                      ></div>
                    ))}
                  </div>
                </div>

                {threeSteps.map((step, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      lottieContainersRef.current[index] = el;
                    }}
                    className={`${styles["rightElement__lottieContainer"]}`}
                  >
                    {index === 0 && animations.create && (
                      <Lottie
                        className={`${styles["rightElement__lottieContainer__lottieAnimation"]}`}
                        animationData={animations.create}
                        loop={false}
                        lottieRef={createLottieRef}
                        autoplay={false}
                      />
                    )}
                    {index === 1 && animations.customize && (
                      <Lottie
                        className={`${styles["rightElement__lottieContainer__lottieAnimation"]}`}
                        animationData={animations.customize}
                        loop={false}
                        lottieRef={customizeLottieRef}
                        autoplay={false}
                      />
                    )}
                    {index === 2 && animations.publish && (
                      <Lottie
                        className={`${styles["rightElement__lottieContainer__lottieAnimation"]}`}
                        animationData={animations.publish}
                        loop={false}
                        lottieRef={publishLottieRef}
                        autoplay={false}
                        onComplete={() => {
                          publishLottieRef.current?.playSegments(
                            [47, 1000],
                            true
                          );
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Version */}
      <div className={`${styles["contentContainer"]} d-none-lg d-flex-xs`}>
        <ProductSteps
          stepsTitle={`Simple Steps.<span style='display: block;'>Endless Possibilities</span>`}
          stepsDescription={`Build your dynamic QR page in 3 simple steps. Just click to add text, images, videos and more exactly as it appears.`}
          createTitle={productSteps?.create_step.create_title}
          createIcon={svgImages.createIcon}
          createSubTitle={productSteps?.create_step.create_subtitle}
          createImage={`/images/home/threeSteps/qrhub-step-create.webp`}
          createDescription={productSteps?.create_step.list}
          customizeTitle={productSteps?.customize_step.customize_title}
          customizeIcon={svgImages.customizeIcon}
          customizeSubTitle={productSteps?.customize_step.customize_subtitle}
          customizeImage={`/images/home/threeSteps/qrhub-step-customize.webp`}
          customizeDescription={productSteps?.customize_step.list}
          publishTitle={productSteps?.publish_step.publish_title}
          publishIcon={svgImages.publishIcon}
          publishSubTitle={productSteps?.publish_step.publish_subtitle}
          publishImage={`/images/home/threeSteps/qrhub-step-publish.webp`}
          publishDescription={productSteps?.publish_step.list}
          buttonTitle={`Try for free`}
          buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL}
          target={"_blank"}
        />
      </div>
    </div>
  );
};
